/**
 * Muse device wire protocol: commands, control replies and the
 * multiplexed sensor stream on characteristic 273e0013.
 *
 * Ported from @musejs/core (Polobase/muse.js, MIT, © 2026 Manuel Haller Polo),
 * whose protocol knowledge in turn credits muse-js, amused-py, OpenMuse and
 * BrainFlow. Cross-checked against OpenMuse's decode.py. See ../NOTICE.md.
 *
 * Two measured corrections from muse.js are deliberate and differ from the
 * published Python decoders: EEG counts are offset by mid-scale (8192) so worn
 * scalp EEG sits near 0 µV, and the 0x88 battery payload is a fixed 20 bytes.
 *
 * Decoding is total: it never throws on malformed input. A headband that emits
 * one bad byte must not take the session down, so problems are counted.
 */

export const MUSE_SERVICE = 0xfe8d;
export const CONTROL_CHARACTERISTIC = "273e0001-4c4d-454d-96be-f03bac821358";
export const DATA_CHARACTERISTIC = "273e0013-4c4d-454d-96be-f03bac821358";

/** Default preset: 4 EEG channels + 4 optics channels (PPG/fNIRS) + IMU + battery. */
export const DEFAULT_PRESET = "p1035";

export const ATHENA_SCALING = Object.freeze({
  eegMicrovoltsPerCount: 1450 / 16383,
  eegZeroCount: 8192,
  accelerometerGPerCount: 0.0000610352,
  gyroscopeDegPerSecondPerCount: -0.0074768,
  opticsPerCount: 1 / 32768,
  batteryPercentDivisor: 256,
});

export type SensorKind = "eeg" | "optics" | "imu" | "battery" | "drlref";

export type SensorSpec = {
  tag: number;
  kind: SensorKind;
  channels: number;
  samples: number;
  rate: number;
  /** Bytes of payload following the tag header. */
  length: number;
};

const SPECS: readonly SensorSpec[] = [
  { tag: 0x11, kind: "eeg", channels: 4, samples: 4, rate: 256, length: 28 },
  { tag: 0x12, kind: "eeg", channels: 8, samples: 2, rate: 256, length: 28 },
  { tag: 0x34, kind: "optics", channels: 4, samples: 3, rate: 64, length: 30 },
  { tag: 0x35, kind: "optics", channels: 8, samples: 2, rate: 64, length: 40 },
  { tag: 0x36, kind: "optics", channels: 16, samples: 1, rate: 64, length: 40 },
  { tag: 0x47, kind: "imu", channels: 6, samples: 3, rate: 52, length: 36 },
  { tag: 0x53, kind: "drlref", channels: 2, samples: 6, rate: 30, length: 24 },
  { tag: 0x88, kind: "battery", channels: 1, samples: 1, rate: 1, length: 20 },
  { tag: 0x98, kind: "battery", channels: 1, samples: 1, rate: 1, length: 20 },
];
const BY_TAG = new Map(SPECS.map((spec) => [spec.tag, spec]));

export function specForTag(tag: number): SensorSpec | undefined { return BY_TAG.get(tag); }

export const PACKET_HEADER_SIZE = 14;
export const SUBPACKET_HEADER_SIZE = 5;

// ───────────────────────────────────────────────────────────────── commands

/**
 * `[length][ASCII command][\n]`, where length counts the command plus its
 * newline (muse-js / OpenMuse convention, confirmed on firmware 3.1.19).
 */
export function encodeCommand(command: string): Uint8Array<ArrayBuffer> {
  if (!command || command.length > 253 || /[^\x20-\x7e]/.test(command)) throw new Error(`Invalid device command "${command}".`);
  const frame = new Uint8Array(new ArrayBuffer(command.length + 2));
  frame[0] = command.length + 1;
  for (let i = 0; i < command.length; i++) frame[i + 1] = command.charCodeAt(i);
  frame[frame.length - 1] = 0x0a;
  return frame;
}

/** Startup after subscribing, with the pause that follows each command (ms). */
export function startupSequence(preset = DEFAULT_PRESET): [string, number][] {
  return [
    ["v6", 200], ["s", 200], ["h", 200],
    [preset, 200], ["s", 200],
    // The first dc001 is ignored by the device; every source agrees.
    ["dc001", 60], ["dc001", 120],
    ["L1", 300], ["s", 0],
  ];
}

/**
 * Control replies arrive as fragments: byte 0 is the fragment's text length.
 * Text accumulates until a closing brace completes a flat JSON object.
 */
export class ReplyAssembler {
  #buffer = "";
  #decoder = new TextDecoder();
  discarded = 0;

  push(fragment: Uint8Array): Record<string, unknown>[] {
    const end = Math.min(fragment.length, 1 + (fragment[0] ?? 0));
    this.#buffer += this.#decoder.decode(fragment.subarray(1, end), { stream: true });
    const replies: Record<string, unknown>[] = [];
    let close = this.#buffer.indexOf("}");
    while (close >= 0) {
      // Replies are flat, so a reply is the last "{" before its "}". A head
      // whose tail was lost must not swallow the next intact reply.
      const open = this.#buffer.lastIndexOf("{", close);
      if (open >= 0) {
        try {
          const parsed = JSON.parse(this.#buffer.slice(open, close + 1));
          if (parsed && typeof parsed === "object") replies.push(parsed as Record<string, unknown>);
        } catch {
          this.discarded++;
        }
      } else this.discarded++;
      this.#buffer = this.#buffer.slice(close + 1);
      close = this.#buffer.indexOf("}");
    }
    if (this.#buffer.length > 4096) {
      this.discarded++;
      this.#buffer = "";
    }
    return replies;
  }
}

// ──────────────────────────────────────────────────────────────── bit fields

/**
 * Unpack `count` unsigned LSB-first fields of `width` bits. Float arithmetic,
 * not `<<`/`>>>`: bitwise operators coerce to signed 32-bit and would corrupt
 * an accumulator crossing bit 31.
 */
export function unpackLE(data: Uint8Array, width: number, count: number): number[] | null {
  if (data.length * 8 < width * count) return null;
  const out = new Array<number>(count);
  const divisor = 2 ** width;
  let acc = 0;
  let bits = 0;
  let position = 0;
  for (let i = 0; i < count; i++) {
    while (bits < width) {
      acc += data[position++]! * 2 ** bits;
      bits += 8;
    }
    const value = acc % divisor;
    acc = (acc - value) / divisor;
    bits -= width;
    out[i] = value;
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────── packets

export type Subpacket = {
  tag: number;
  kind: SensorKind;
  spec: SensorSpec;
  /** Per-sensor sequence counter (the leading subpacket's is in the packet header). */
  sequence: number;
  data: Uint8Array;
};

export type AthenaPacket = {
  length: number;
  /** u16 counter, +1 per packet: the authoritative lost-notification signal. */
  counter: number;
  subpackets: Subpacket[];
};

export type DecodeResult = { packets: AthenaPacket[]; unknownTags: number; malformed: number };

/**
 * Split one notification into packets and subpackets:
 *
 *   packet[]  (declared length in byte 0)
 *     14-byte header; [1..2] u16 counter, [9] leading tag, [10] its sequence
 *     leading subpacket payload (no header of its own)
 *     [tag][sequence][3 reserved][payload], repeated
 *
 * Header byte 13 is deliberately not validated: it is non-zero on current
 * firmware and treating it as corruption breaks the Python decoders there.
 */
export function decodeNotification(payload: Uint8Array): DecodeResult {
  const packets: AthenaPacket[] = [];
  let unknownTags = 0;
  let malformed = 0;
  let offset = 0;
  while (offset < payload.length) {
    const length = payload[offset]!;
    if (offset + PACKET_HEADER_SIZE > payload.length || length < PACKET_HEADER_SIZE || offset + length > payload.length) {
      malformed++;
      break; // A tiny or overlong length cannot be advanced past safely.
    }
    const end = offset + length;
    const packet: AthenaPacket = { length, counter: payload[offset + 1]! + payload[offset + 2]! * 0x100, subpackets: [] };
    let cursor = offset + PACKET_HEADER_SIZE;
    const lead = specForTag(payload[offset + 9]!);
    if (!lead) {
      unknownTags++;
      cursor = end;
    } else if (cursor + lead.length <= end) {
      packet.subpackets.push({ tag: lead.tag, kind: lead.kind, spec: lead, sequence: payload[offset + 10]!, data: payload.subarray(cursor, cursor + lead.length) });
      cursor += lead.length;
    } else {
      malformed++;
      cursor = end;
    }
    while (cursor + SUBPACKET_HEADER_SIZE <= end) {
      const spec = specForTag(payload[cursor]!);
      if (!spec) {
        unknownTags++;
        break; // Length unknown, so the walk cannot continue past it.
      }
      const start = cursor + SUBPACKET_HEADER_SIZE;
      if (start + spec.length > end) {
        malformed++;
        break;
      }
      packet.subpackets.push({ tag: spec.tag, kind: spec.kind, spec, sequence: payload[cursor + 1]!, data: payload.subarray(start, start + spec.length) });
      cursor = start + spec.length;
    }
    packets.push(packet);
    offset = end;
  }
  return { packets, unknownTags, malformed };
}

// ───────────────────────────────────────────────────────── physical values

/** Sample-major counts → per-channel arrays of µV. */
export function decodeEeg(sub: Subpacket): Float32Array[] | null {
  const { channels, samples } = sub.spec;
  const counts = unpackLE(sub.data, 14, channels * samples);
  if (!counts) return null;
  const out = Array.from({ length: channels }, () => new Float32Array(samples));
  for (let s = 0; s < samples; s++) {
    for (let c = 0; c < channels; c++) out[c]![s] = (counts[s * channels + c]! - ATHENA_SCALING.eegZeroCount) * ATHENA_SCALING.eegMicrovoltsPerCount;
  }
  return out;
}

/** Sample-major 20-bit counts → per-channel arrays in arbitrary units. */
export function decodeOptics(sub: Subpacket): Float32Array[] | null {
  const { channels, samples } = sub.spec;
  const counts = unpackLE(sub.data, 20, channels * samples);
  if (!counts) return null;
  const out = Array.from({ length: channels }, () => new Float32Array(samples));
  for (let s = 0; s < samples; s++) {
    for (let c = 0; c < channels; c++) out[c]![s] = counts[s * channels + c]! * ATHENA_SCALING.opticsPerCount;
  }
  return out;
}

/** Battery state of charge (%), from the first two bytes of 0x88 / 0x98. */
export function decodeBattery(sub: Subpacket): number | null {
  if (sub.data.length < 2) return null;
  const percent = (sub.data[0]! + sub.data[1]! * 0x100) / ATHENA_SCALING.batteryPercentDivisor;
  return Number.isFinite(percent) ? Math.max(0, Math.min(100, percent)) : null;
}

/** IMU: per sample ax ay az (g) gx gy gz (deg/s). */
export function decodeImu(sub: Subpacket): Float32Array[] | null {
  const { channels, samples } = sub.spec;
  if (sub.data.length < channels * samples * 2) return null;
  const out = Array.from({ length: channels }, () => new Float32Array(samples));
  for (let s = 0; s < samples; s++) {
    for (let c = 0; c < channels; c++) {
      const i = (s * channels + c) * 2;
      const unsigned = sub.data[i]! + sub.data[i + 1]! * 0x100;
      const signed = unsigned >= 0x8000 ? unsigned - 0x10000 : unsigned;
      out[c]![s] = signed * (c < 3 ? ATHENA_SCALING.accelerometerGPerCount : ATHENA_SCALING.gyroscopeDegPerSecondPerCount);
    }
  }
  return out;
}

/** Packet counter difference, accounting for u16 wrap. 1 means none lost. */
export function counterStep(previous: number, next: number): number {
  return (next - previous + 0x10000) % 0x10000;
}
